import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, TrendingUp, Clock, Lightbulb, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const HOURS = Array.from({ length: 24 }, (_, i) => `${i}h`);

function getHeatColor(value, max) {
  if (value === 0) return 'bg-muted/40 text-muted-foreground/40';
  const ratio = value / Math.max(max, 1);
  if (ratio > 0.75) return 'bg-red-500 text-white font-bold';
  if (ratio > 0.5) return 'bg-orange-500 text-white font-semibold';
  if (ratio > 0.25) return 'bg-amber-400 text-black font-medium';
  return 'bg-emerald-500 text-white';
}

export function HeatmapPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/heatmap?days=${period}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-[400px] w-full" /></div>;

  const heatmap = data?.heatmap || [];
  const maxVal = data?.max_value || 1;

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Mapa de Calor de Incidentes</h2>
          <p className="text-sm text-muted-foreground">{data?.total_events || 0} incidentes no período</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-32"><Calendar className="h-3.5 w-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Heatmap Grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <Clock className="h-4 w-4" />Incidentes por Dia x Hora
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="min-w-[700px]">
            {/* Hour labels */}
            <div className="flex">
              <div className="w-12 shrink-0" />
              {HOURS.map(h => (
                <div key={h} className="flex-1 text-center text-xs text-muted-foreground pb-1">{h}</div>
              ))}
              <div className="w-16 shrink-0 text-center text-xs text-muted-foreground pb-1">Total</div>
            </div>
            {/* Grid rows */}
            {heatmap.map((row) => (
              <div key={row.day} className="flex items-center gap-0.5 mb-0.5">
                <div className="w-12 shrink-0 text-xs font-medium text-muted-foreground text-right pr-2">{DAYS[row.day]}</div>
                {row.hours.map((val, h) => (
                  <div
                    key={h}
                    className={`flex-1 aspect-square flex items-center justify-center text-xs rounded-sm transition-colors ${getHeatColor(val, maxVal)}`}
                    title={`${DAYS[row.day]} ${h}:00 — ${val} incidentes`}
                  >
                    {val > 0 ? val : ''}
                  </div>
                ))}
                <div className="w-16 shrink-0 text-center text-xs font-semibold">{row.total}</div>
              </div>
            ))}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
            <span>Legenda:</span>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-sm bg-muted/40" /><span>0</span></div>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-sm bg-emerald-500" /><span>Baixo</span></div>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-sm bg-amber-400" /><span>Médio</span></div>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-sm bg-orange-500" /><span>Alto</span></div>
            <div className="flex items-center gap-1"><div className="w-4 h-4 rounded-sm bg-red-500" /><span>Crítico</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />Tendência Mensal (12 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const monthly = data?.monthly || [];
            const maxMonth = Math.max(...monthly.map(m => m.count), 1);
            return (
              <div className="flex items-end justify-between gap-1 h-40">
                {monthly.map((m, i) => {
                  const pct = (m.count / maxMonth) * 100;
                  const prev = i > 0 ? monthly[i-1].count : 0;
                  const change = prev > 0 ? ((m.count - prev) / prev * 100).toFixed(1) : null;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      {m.count > 0 && <span className="text-xs font-bold">{m.count}</span>}
                      {change && parseFloat(change) > 0 && <span className="text-xs text-red-500">+{change}%</span>}
                      <div
                        className="w-full rounded-t transition-all"
                        style={{
                          height: `${Math.max(pct, m.count > 0 ? 4 : 0)}%`,
                          backgroundColor: m.count === 0 ? '#E5E7EB' : i === monthly.length - 1 ? '#F97316' : '#3B82F6'
                        }}
                      />
                      <span className="text-xs text-muted-foreground">{m.label}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Recommendations */}
      {(data?.recommendations || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />Recomendações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recommendations.map((rec, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded border bg-amber-50/50 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
